import { useCallback, useMemo } from "react";
import { useTellescopeSession, type UsePermissionQueryModel } from "@tellescope/tanstack-query";
import { CONTACT_DETAILS_BLOCKS_FLAT_MAP } from "../constants";
import type { BlockBaseConfig, ContactDetailsFeature } from "../types";
import { isBlockAvailable, isRegisteredBlockType } from "../utils";

/**
 * Every model any block declares as required reading, deduped. Derived from the registry at module
 * scope so it cannot drift from the block definitions.
 */
const GATED_MODELS: readonly UsePermissionQueryModel[] = [
  ...new Set(
    Object.values<BlockBaseConfig>(CONTACT_DETAILS_BLOCKS_FLAT_MAP).flatMap((config) => config.requiredReadModels ?? [])
  ),
];

export interface UseBlockAvailabilityArgs {
  /** Injected org-feature gate, owned by the app. Omitted ⇒ the feature half of the gate passes. */
  isFeatureEnabled?: (feature: ContactDetailsFeature) => boolean;
}

export interface UseBlockAvailabilityResult {
  /**
   * Whether a block should be offered and rendered for the current viewer. Accepts any string so
   * callers can pass a persisted `block.type` straight from the model without narrowing first; a type
   * that isn't in the registry is reported unavailable (an unknown block cannot be rendered anyway).
   */
  isAvailable: (blockType: string) => boolean;
}

/**
 * Resolves which contact-details blocks the current viewer may use, combining read permissions with
 * the app's org-feature gating.
 *
 * Reads the session's access map directly rather than calling `usePermissionQuery` per model: blocks
 * depend on a varying number of models, and a hook cannot be called in a variable-length loop. A
 * capability check is also the right question for visibility — whether the viewer may read the model
 * at all — since the individual rows a block renders are access-filtered by the backend regardless.
 *
 * A model is readable when the session grants any non-null access type for `read`, matching
 * `usePermissionQuery`'s record-free behavior; super-admins bypass row filtering entirely.
 */
export const useBlockAvailability = ({
  isFeatureEnabled,
}: UseBlockAvailabilityArgs = {}): UseBlockAvailabilityResult => {
  const session = useTellescopeSession();

  const readableModels = useMemo(() => {
    const userInfo = session?.userInfo;
    const access = userInfo && "access" in userInfo ? userInfo.access : undefined;
    const isSuperAdmin = !!userInfo && "isa" in userInfo && !!userInfo.isa;

    return GATED_MODELS.reduce<Partial<Record<UsePermissionQueryModel, boolean>>>((acc, model) => {
      acc[model] = isSuperAdmin || !!access?.[model]?.read;
      return acc;
    }, {});
  }, [session]);

  const isAvailable = useCallback(
    (blockType: string) => {
      if (!isRegisteredBlockType(blockType)) return false;
      return isBlockAvailable(blockType, { readableModels, isFeatureEnabled });
    },
    [isFeatureEnabled, readableModels]
  );

  return { isAvailable };
};
